"use client";

import { useContext } from "react";
import classNames from "classnames";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";

import { FooterCopyright } from "@/components/Footer/FooterCopyright";
import { Trustpilot } from "@/components/Trustpilot/Trustpilot";
import { Typography } from "@/components/Typography";
import { Link } from "@/components/Link";
import { Logo } from "@/components/Logo";

import { paths } from "@/utils/paths";
import { getSiteInfo } from "@/sites";

import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import { FooterProps } from "./types";

export const Footer = ({
  footerCTAHref,
  noMb,
  customClass,
  hideTrustpilot = false,
}: FooterProps) => {
  const { t } = useTranslation("layout");

  const { siteBlackLogoFooter, siteName, showTrustpilot } =
    getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!) || {};

  const router = useRouter();
  const { setSource } = useContext(AmplitudeContext);

  const classnames = classNames("footer", {
    footerNoMB: noMb,
    [`${customClass}`]: customClass,
  });

  const onCreateQrCodeClick = () => {
    setSource("link_footer_create");
    router.push(footerCTAHref);
  };

  const onPricesClick = () => {
    setSource("footer_plan_&_pricing");
    router.push(footerCTAHref);
  };

  const getTrustpilotComponent = () => {
    if (showTrustpilot && !hideTrustpilot) {
      return (
        <div className="trustpilot">
          <Trustpilot />
        </div>
      );
    }

    return null;
  };

  return (
    <footer
      className={classnames}
      data-testid="footer"
    >
      <div className="footer-grid">
        <div className="footer-col logo-col">
          <Logo
            src={siteBlackLogoFooter}
            siteName={siteName}
            dataQa="footer-logo-link"
            dataTestId="footer-logo"
            width={280}
            height={49}
            loading="lazy"
          />
        </div>

        <div className="footer-col cta-col">
          <div className="cta-section">
            <Typography
              Tag="p"
              size="l"
              classname="cta-section-text"
            >
              {t("footer_text")}
            </Typography>

            <ButtonLib
              id="footer-qr-editor-button"
              variant="secondary"
              label={t("footer_button_label")}
              dataQA="footer-qr-editor-button"
              onClick={onCreateQrCodeClick}
            />
          </div>

          {getTrustpilotComponent()}
        </div>

        <div className="nav-columns">
          <div className="footer-col company-col">
            <h4 className="footer-links-title"> {t("footer_company_text")}</h4>
            <ul className="footer-links">
              <li>
                <Link
                  href={paths.whoWeAre()}
                  dataQA="footer-who-we-are-button"
                >
                  {t("footer_who_we_are_text")}
                </Link>
              </li>
              <li>
                <Link
                  href={paths.termsAndConditions()}
                  dataQA="footer-terms-and-conditions-button"
                >
                  {t("footer_terms_and_conditions_text")}
                </Link>
              </li>
              <li>
                <Link
                  href={paths.termsOfUse()}
                  dataQA="footer-terms-of-use-button"
                >
                  {t("footer_terms_of_use_text")}
                </Link>
              </li>
              <li>
                <Link
                  href={paths.privacyPolicy()}
                  dataQA="footer-privacy-policy-button"
                >
                  {t("footer_privacy_policy_text")}
                </Link>
              </li>
              <li>
                <Link
                  href={paths.cookiesPolicy()}
                  dataQA="footer-cookies-policy-button"
                >
                  {t("footer_cookies_policy_text")}
                </Link>
              </li>
              <li>
                <Link
                  href={paths.pricing()}
                  dataQA="footer-prices-button"
                  onClick={onPricesClick}
                >
                  {t("footer_prices_text")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="footer-col help-col">
            <h4 className="footer-links-title"> {t("footer_need_help_text")}</h4>
            <ul className="footer-links">
              <li>
                <Link
                  href={paths.contactUs()}
                  dataQA="footer-contact-us-button"
                >
                  {t("footer_contact_us_text")}
                </Link>
              </li>
              <li>
                <Link
                  href={paths.faq()}
                  dataQA="footer-faq-button"
                >
                  {t("footer_faq_text")}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <FooterCopyright />
    </footer>
  );
};
